import { useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import {
  useAdminUsers,
  useAdminUserDetails,
  useWarnUser,
  useSuspendUser,
  useBanUser,
  useUnbanUser,
  useChangeUserRole,
  useUpdateUserProfile,
  useRestrictProfileField,
  useLiftProfileRestriction,
  useUpdateUserModeration,
} from '../../hooks/use-admin';
import {
  Users,
  Search,
  Filter,
  AlertTriangle,
  Ban,
  Clock,
  Shield,
  ShieldCheck,
  MoreVertical,
  Eye,
  ChevronLeft,
  ChevronRight,
  Edit,
  Lock,
  Unlock,
  User,
  Image,
  FileText,
  MessageSquare,
  ThumbsUp,
  Flag,
  X,
  Check,
  History,
  Settings,
  UserCog,
} from 'lucide-react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from '../../components/ui/avatar';
import { Badge } from '../../components/ui/badge';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../components/ui/tabs';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import { cn } from '../../lib/utils';
import { useToast } from '../../hooks/use-toast';

// ===================== USER EDITOR DIALOG ===================== //

function UserEditorDialog({ user, open, onClose }) {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState('profile');
  const [restrictReason, setRestrictReason] = useState('');
  const [restrictField, setRestrictField] = useState(null);
  const [editedProfile, setEditedProfile] = useState({});
  const [editReason, setEditReason] = useState('');
  const [moderationSettings, setModerationSettings] = useState({});

  // Fetch full user details
  const {
    data: userDetails,
    isLoading: loadingDetails,
    refetch,
  } = useAdminUserDetails(user?.creator || user?.walletAddress);

  // Mutations
  const updateProfile = useUpdateUserProfile();
  const restrictField_ = useRestrictProfileField();
  const liftRestriction = useLiftProfileRestriction();
  const updateModeration = useUpdateUserModeration();

  const fullUser = userDetails?.user || user;
  // profileRestrictions is nested under moderation in the User model
  const restrictions = fullUser?.moderation?.profileRestrictions || {};

  // Initialize edited profile when user data loads
  useState(() => {
    if (fullUser) {
      setEditedProfile({
        username: fullUser.username || '',
        bio: fullUser.bio || '',
        location: fullUser.location || '',
        twitter: fullUser.socials?.twitter || '',
        discord: fullUser.socials?.discord || '',
        website: fullUser.socials?.website || '',
      });
      setModerationSettings({
        canPost: fullUser.moderation?.canPost ?? true,
        canComment: fullUser.moderation?.canComment ?? true,
        canVote: fullUser.moderation?.canVote ?? true,
        canReport: fullUser.moderation?.canReport ?? true,
        internalNotes: fullUser.moderation?.internalNotes || '',
      });
    }
  }, [fullUser]);

  const handleSaveProfile = async () => {
    if (!editReason.trim()) {
      toast({
        title: 'Please provide a reason for changes',
        variant: 'destructive',
      });
      return;
    }

    try {
      await updateProfile.mutateAsync({
        userId: fullUser.creator,
        profileData: editedProfile,
        reason: editReason,
      });
      setEditReason('');
      refetch();
    } catch (err) {
      // Error handled by hook
    }
  };

  const handleRestrict = async () => {
    if (!restrictField || !restrictReason.trim()) {
      toast({
        title: 'Please provide a reason for restriction',
        variant: 'destructive',
      });
      return;
    }

    try {
      await restrictField_.mutateAsync({
        userId: fullUser.creator,
        field: restrictField,
        reason: restrictReason,
      });
      setRestrictField(null);
      setRestrictReason('');
      refetch();
    } catch (err) {
      // Error handled by hook
    }
  };

  const handleLiftRestriction = async (field, restoreOriginal = false) => {
    try {
      await liftRestriction.mutateAsync({
        userId: fullUser.creator,
        field,
        restoreOriginal,
      });
      refetch();
    } catch (err) {
      // Error handled by hook
    }
  };

  const handleSaveModeration = async () => {
    try {
      await updateModeration.mutateAsync({
        userId: fullUser.creator,
        moderationSettings,
        reason: 'Admin updated moderation settings',
      });
      refetch();
    } catch (err) {
      // Error handled by hook
    }
  };

  // Map field names to the flat structure in the model
  const getRestrictionInfo = (field) => {
    const fieldMap = {
      avatar: {
        restricted: 'avatarRestricted',
        at: 'avatarRestrictedAt',
        by: 'avatarRestrictedBy',
        reason: 'avatarRestrictedReason',
        banned: 'avatarBannedUrl',
      },
      background: {
        restricted: 'backgroundRestricted',
        at: 'backgroundRestrictedAt',
        by: 'backgroundRestrictedBy',
        reason: 'backgroundRestrictedReason',
        banned: 'backgroundBannedUrl',
      },
      bio: {
        restricted: 'bioRestricted',
        at: 'bioRestrictedAt',
        by: 'bioRestrictedBy',
        reason: 'bioRestrictedReason',
        banned: 'bioBannedContent',
      },
      username: {
        restricted: 'usernameRestricted',
        at: 'usernameRestrictedAt',
        by: 'usernameRestrictedBy',
        reason: 'usernameRestrictedReason',
        banned: 'usernameBannedContent',
      },
    };
    const fm = fieldMap[field];
    return {
      isRestricted: restrictions[fm.restricted] || false,
      restrictedAt: restrictions[fm.at],
      restrictedBy: restrictions[fm.by],
      reason: restrictions[fm.reason],
      bannedContent: restrictions[fm.banned],
    };
  };

  const RestrictionBadge = ({ field, label }) => {
    const info = getRestrictionInfo(field);
    const isRestricted = info.isRestricted;

    return (
      <div className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg border border-zinc-700">
        <div className="flex items-center gap-3">
          <div
            className={cn(
              'p-2 rounded-full',
              isRestricted
                ? 'bg-red-500/20 text-red-400'
                : 'bg-green-500/20 text-green-400'
            )}
          >
            {isRestricted ? (
              <Lock className="h-4 w-4" />
            ) : (
              <Unlock className="h-4 w-4" />
            )}
          </div>
          <div>
            <p className="text-sm font-medium text-white capitalize">{label}</p>
            {isRestricted && (
              <p className="text-xs text-red-400">Restricted: {info.reason}</p>
            )}
            {isRestricted && info.restrictedAt && (
              <p className="text-xs text-zinc-500">
                Since {new Date(info.restrictedAt).toLocaleDateString()}
              </p>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          {isRestricted ? (
            <>
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleLiftRestriction(field, true)}
                disabled={liftRestriction.isPending}
                className="border-zinc-600 text-green-400 hover:bg-green-500/10"
              >
                Restore Original
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleLiftRestriction(field, false)}
                disabled={liftRestriction.isPending}
                className="border-zinc-600 text-blue-400 hover:bg-blue-500/10"
              >
                Lift Only
              </Button>
            </>
          ) : (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setRestrictField(field)}
              className="border-zinc-600 text-red-400 hover:bg-red-500/10"
            >
              <Lock className="h-3 w-3 mr-1" />
              Restrict
            </Button>
          )}
        </div>
      </div>
    );
  };

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <Avatar className="h-10 w-10">
              <AvatarImage src={fullUser?.profilePicture} />
              <AvatarFallback className="bg-zinc-800">
                {fullUser?.username?.slice(0, 2).toUpperCase() || '??'}
              </AvatarFallback>
            </Avatar>
            <div>
              <span className="text-lg">{fullUser?.username || 'User'}</span>
              <p className="text-xs text-zinc-500 font-normal">
                {fullUser?.creator || fullUser?.walletAddress}
              </p>
            </div>
          </DialogTitle>
        </DialogHeader>

        {loadingDetails ? (
          <div className="py-8 text-center">
            <div className="animate-spin h-8 w-8 border-2 border-blue-500 border-t-transparent rounded-full mx-auto" />
          </div>
        ) : (
          <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-4">
            <TabsList className="bg-zinc-800 border-zinc-700 w-full justify-start">
              <TabsTrigger
                value="profile"
                className="data-[state=active]:bg-zinc-700"
              >
                <User className="h-4 w-4 mr-2" />
                Profile
              </TabsTrigger>
              <TabsTrigger
                value="restrictions"
                className="data-[state=active]:bg-zinc-700"
              >
                <Lock className="h-4 w-4 mr-2" />
                Restrictions
              </TabsTrigger>
              <TabsTrigger
                value="moderation"
                className="data-[state=active]:bg-zinc-700"
              >
                <Settings className="h-4 w-4 mr-2" />
                Moderation
              </TabsTrigger>
              <TabsTrigger
                value="activity"
                className="data-[state=active]:bg-zinc-700"
              >
                <History className="h-4 w-4 mr-2" />
                Activity
              </TabsTrigger>
            </TabsList>

            {/* Profile Tab */}
            <TabsContent value="profile" className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                {/* Profile Preview */}
                <Card className="border-zinc-700 bg-zinc-800/50">
                  <CardHeader>
                    <CardTitle className="text-sm text-zinc-400">
                      Current Profile
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="relative">
                      <div
                        className="h-24 rounded-lg bg-cover bg-center"
                        style={{
                          backgroundImage: fullUser?.backgroundImage
                            ? `url(${fullUser.backgroundImage})`
                            : 'linear-gradient(to right, #3b82f6, #8b5cf6)',
                        }}
                      />
                      <Avatar className="h-16 w-16 absolute -bottom-8 left-4 border-4 border-zinc-800">
                        <AvatarImage src={fullUser?.profilePicture} />
                        <AvatarFallback className="bg-zinc-700">
                          {fullUser?.username?.slice(0, 2).toUpperCase() ||
                            '??'}
                        </AvatarFallback>
                      </Avatar>
                    </div>
                    <div className="pt-10">
                      <p className="font-semibold text-white">
                        {fullUser?.username || 'Anonymous'}
                      </p>
                      <p className="text-sm text-zinc-400 mt-1">
                        {fullUser?.bio || 'No bio set'}
                      </p>
                      {fullUser?.location && (
                        <p className="text-xs text-zinc-500 mt-2">
                          📍 {fullUser.location}
                        </p>
                      )}
                    </div>
                    {restrictions?.bioRestricted && (
                      <div className="p-2 bg-red-500/10 border border-red-500/30 rounded text-xs text-red-400">
                        ⚠️ Bio restricted by admin:{' '}
                        {restrictions.bioRestrictedReason}
                      </div>
                    )}
                    {restrictions?.avatarRestricted && (
                      <div className="p-2 bg-red-500/10 border border-red-500/30 rounded text-xs text-red-400">
                        ⚠️ Profile picture restricted by admin
                      </div>
                    )}
                    {restrictions?.backgroundRestricted && (
                      <div className="p-2 bg-red-500/10 border border-red-500/30 rounded text-xs text-red-400">
                        ⚠️ Background image restricted by admin
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Edit Profile */}
                <Card className="border-zinc-700 bg-zinc-800/50">
                  <CardHeader>
                    <CardTitle className="text-sm text-zinc-400">
                      Edit Profile
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div>
                      <Label className="text-zinc-400">Username</Label>
                      <Input
                        value={
                          editedProfile.username || fullUser?.username || ''
                        }
                        onChange={(e) =>
                          setEditedProfile((p) => ({
                            ...p,
                            username: e.target.value,
                          }))
                        }
                        className="mt-1 bg-zinc-900 border-zinc-700"
                        disabled={restrictions?.usernameRestricted}
                      />
                    </div>
                    <div>
                      <Label className="text-zinc-400">Bio</Label>
                      <Textarea
                        value={editedProfile.bio || fullUser?.bio || ''}
                        onChange={(e) =>
                          setEditedProfile((p) => ({
                            ...p,
                            bio: e.target.value,
                          }))
                        }
                        className="mt-1 bg-zinc-900 border-zinc-700"
                        rows={2}
                        disabled={restrictions?.bioRestricted}
                      />
                    </div>
                    <div>
                      <Label className="text-zinc-400">Location</Label>
                      <Input
                        value={
                          editedProfile.location || fullUser?.location || ''
                        }
                        onChange={(e) =>
                          setEditedProfile((p) => ({
                            ...p,
                            location: e.target.value,
                          }))
                        }
                        className="mt-1 bg-zinc-900 border-zinc-700"
                      />
                    </div>
                    <div>
                      <Label className="text-zinc-400">
                        Reason for Changes
                      </Label>
                      <Textarea
                        value={editReason}
                        onChange={(e) => setEditReason(e.target.value)}
                        className="mt-1 bg-zinc-900 border-zinc-700"
                        rows={2}
                        placeholder="Why are you making these changes?"
                      />
                    </div>
                    <Button
                      onClick={handleSaveProfile}
                      disabled={updateProfile.isPending || !editReason}
                      className="w-full bg-blue-600 hover:bg-blue-700"
                    >
                      {updateProfile.isPending ? 'Saving...' : 'Save Changes'}
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Restrictions Tab */}
            <TabsContent value="restrictions" className="space-y-4 mt-4">
              <div className="space-y-3">
                <h3 className="text-sm font-medium text-zinc-400 mb-3">
                  Profile Field Restrictions
                </h3>
                <p className="text-xs text-zinc-500 mb-4">
                  Restrict specific profile fields to prevent the user from
                  changing them. The original value is preserved and can be
                  restored when lifting the restriction.
                </p>

                <RestrictionBadge field="avatar" label="Profile Picture" />
                <RestrictionBadge field="banner" label="Background Image" />
                <RestrictionBadge field="bio" label="Bio" />
                <RestrictionBadge field="username" label="Username" />
              </div>

              {/* Restrict Field Dialog */}
              <Dialog
                open={!!restrictField}
                onOpenChange={() => setRestrictField(null)}
              >
                <DialogContent className="bg-zinc-900 border-zinc-800 text-white">
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                      <Lock className="h-5 w-5 text-red-400" />
                      Restrict {restrictField}
                    </DialogTitle>
                    <DialogDescription className="text-zinc-400">
                      This will prevent the user from modifying their{' '}
                      {restrictField}. The current value will be saved and can
                      be restored later.
                    </DialogDescription>
                  </DialogHeader>
                  <div>
                    <Label className="text-zinc-400">
                      Reason for Restriction
                    </Label>
                    <Textarea
                      value={restrictReason}
                      onChange={(e) => setRestrictReason(e.target.value)}
                      className="mt-1 bg-zinc-800 border-zinc-700"
                      placeholder="e.g., Inappropriate content, violation of ToS..."
                      rows={3}
                    />
                  </div>
                  <DialogFooter>
                    <Button
                      variant="ghost"
                      onClick={() => setRestrictField(null)}
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleRestrict}
                      disabled={!restrictReason || restrictField_.isPending}
                      className="bg-red-500 hover:bg-red-600"
                    >
                      {restrictField_.isPending
                        ? 'Restricting...'
                        : 'Restrict Field'}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </TabsContent>

            {/* Moderation Tab */}
            <TabsContent value="moderation" className="space-y-4 mt-4">
              <Card className="border-zinc-700 bg-zinc-800/50">
                <CardHeader>
                  <CardTitle className="text-sm text-zinc-400">
                    User Permissions
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <MessageSquare className="h-4 w-4 text-zinc-400" />
                      <div>
                        <p className="text-sm text-white">Can Post</p>
                        <p className="text-xs text-zinc-500">
                          Allow user to create discussions
                        </p>
                      </div>
                    </div>
                    <Switch
                      checked={
                        moderationSettings.canPost ??
                        fullUser?.moderation?.canPost ??
                        true
                      }
                      onCheckedChange={(v) =>
                        setModerationSettings((s) => ({ ...s, canPost: v }))
                      }
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <MessageSquare className="h-4 w-4 text-zinc-400" />
                      <div>
                        <p className="text-sm text-white">Can Comment</p>
                        <p className="text-xs text-zinc-500">
                          Allow user to comment on discussions
                        </p>
                      </div>
                    </div>
                    <Switch
                      checked={
                        moderationSettings.canComment ??
                        fullUser?.moderation?.canComment ??
                        true
                      }
                      onCheckedChange={(v) =>
                        setModerationSettings((s) => ({ ...s, canComment: v }))
                      }
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <ThumbsUp className="h-4 w-4 text-zinc-400" />
                      <div>
                        <p className="text-sm text-white">Can Vote</p>
                        <p className="text-xs text-zinc-500">
                          Allow user to upvote/downvote
                        </p>
                      </div>
                    </div>
                    <Switch
                      checked={
                        moderationSettings.canVote ??
                        fullUser?.moderation?.canVote ??
                        true
                      }
                      onCheckedChange={(v) =>
                        setModerationSettings((s) => ({ ...s, canVote: v }))
                      }
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Flag className="h-4 w-4 text-zinc-400" />
                      <div>
                        <p className="text-sm text-white">Can Report</p>
                        <p className="text-xs text-zinc-500">
                          Allow user to report content
                        </p>
                      </div>
                    </div>
                    <Switch
                      checked={
                        moderationSettings.canReport ??
                        fullUser?.moderation?.canReport ??
                        true
                      }
                      onCheckedChange={(v) =>
                        setModerationSettings((s) => ({ ...s, canReport: v }))
                      }
                    />
                  </div>
                </CardContent>
              </Card>

              <Card className="border-zinc-700 bg-zinc-800/50">
                <CardHeader>
                  <CardTitle className="text-sm text-zinc-400">
                    Internal Notes
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <Textarea
                    value={
                      moderationSettings.internalNotes ??
                      fullUser?.moderation?.internalNotes ??
                      ''
                    }
                    onChange={(e) =>
                      setModerationSettings((s) => ({
                        ...s,
                        internalNotes: e.target.value,
                      }))
                    }
                    className="bg-zinc-900 border-zinc-700"
                    placeholder="Private notes about this user (only visible to admins)..."
                    rows={4}
                  />
                </CardContent>
              </Card>

              <Button
                onClick={handleSaveModeration}
                disabled={updateModeration.isPending}
                className="w-full bg-blue-600 hover:bg-blue-700"
              >
                {updateModeration.isPending
                  ? 'Saving...'
                  : 'Save Moderation Settings'}
              </Button>
            </TabsContent>

            {/* Activity Tab */}
            <TabsContent value="activity" className="space-y-4 mt-4">
              <Card className="border-zinc-700 bg-zinc-800/50">
                <CardHeader>
                  <CardTitle className="text-sm text-zinc-400">
                    Moderation History
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {fullUser?.moderation?.warnings?.length > 0 ? (
                    <div className="space-y-3">
                      {fullUser.moderation.warnings.map((warning, i) => (
                        <div
                          key={i}
                          className="p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg"
                        >
                          <div className="flex items-center justify-between">
                            <Badge className="bg-yellow-500/20 text-yellow-400">
                              Warning
                            </Badge>
                            <span className="text-xs text-zinc-500">
                              {new Date(warning.issuedAt).toLocaleString()}
                            </span>
                          </div>
                          <p className="text-sm text-zinc-300 mt-2">
                            {warning.reason}
                          </p>
                          <p className="text-xs text-zinc-500 mt-1">
                            By: {warning.issuedBy}
                          </p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-zinc-500">
                      No moderation history
                    </p>
                  )}
                </CardContent>
              </Card>

              <Card className="border-zinc-700 bg-zinc-800/50">
                <CardHeader>
                  <CardTitle className="text-sm text-zinc-400">
                    Account Stats
                  </CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-zinc-500">Member Since</p>
                    <p className="text-sm text-white">
                      {fullUser?.createdAt
                        ? new Date(fullUser.createdAt).toLocaleDateString()
                        : 'Unknown'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-zinc-500">Last Active</p>
                    <p className="text-sm text-white">
                      {fullUser?.lastActive
                        ? new Date(fullUser.lastActive).toLocaleString()
                        : 'Unknown'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-zinc-500">Total Posts</p>
                    <p className="text-sm text-white">
                      {fullUser?.stats?.totalPosts || 0}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-zinc-500">Total Comments</p>
                    <p className="text-sm text-white">
                      {fullUser?.stats?.totalComments || 0}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="text-zinc-400">
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function UserRow({ user, onAction }) {
  const roleBadgeVariants = {
    admin: 'bg-red-500/20 text-red-400 border-red-500/30',
    moderator: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    user: 'bg-zinc-500/20 text-zinc-400 border-zinc-500/30',
  };

  // Backend returns 'creator' field for wallet address
  const walletAddress = user.creator || user.walletAddress;

  const statusBadge = () => {
    if (user.moderation?.isBanned) {
      return (
        <Badge
          variant="destructive"
          className="bg-red-500/20 text-red-400 border-red-500/30"
        >
          Banned
        </Badge>
      );
    }
    if (user.moderation?.isSuspended) {
      return (
        <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30">
          Suspended
        </Badge>
      );
    }
    return (
      <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
        Active
      </Badge>
    );
  };

  return (
    <div className="flex items-center justify-between p-4 border-b border-zinc-800 hover:bg-zinc-900/50 transition-colors">
      <div className="flex items-center gap-4 flex-1">
        <Avatar className="h-10 w-10">
          <AvatarImage src={user.profilePicture} alt={user.username} />
          <AvatarFallback className="bg-zinc-800 text-zinc-400">
            {user.username?.slice(0, 2).toUpperCase() || '??'}
          </AvatarFallback>
        </Avatar>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <Link
              to={`/profile/${walletAddress}`}
              className="text-white font-medium hover:text-blue-400 transition-colors truncate"
            >
              {user.username || 'Anonymous'}
            </Link>
            <Badge
              variant="outline"
              className={cn('text-xs', roleBadgeVariants[user.role])}
            >
              {user.role}
            </Badge>
          </div>
          <p className="text-xs text-zinc-500 truncate">{walletAddress}</p>
        </div>
      </div>

      <div className="flex items-center gap-4">
        <div className="hidden md:flex flex-col items-end text-sm">
          <span className="text-zinc-400">Joined</span>
          <span className="text-zinc-500 text-xs">
            {new Date(user.createdAt).toLocaleDateString()}
          </span>
        </div>

        <div className="hidden sm:block">{statusBadge()}</div>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-zinc-400 hover:text-white"
            >
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            className="bg-zinc-900 border-zinc-800"
          >
            <DropdownMenuItem
              className="text-zinc-300 focus:bg-zinc-800 cursor-pointer"
              onClick={() => onAction('view', user)}
            >
              <Eye className="h-4 w-4 mr-2" />
              View Details
            </DropdownMenuItem>
            <DropdownMenuItem
              className="text-blue-400 focus:bg-zinc-800 cursor-pointer"
              onClick={() => onAction('edit', user)}
            >
              <UserCog className="h-4 w-4 mr-2" />
              Edit User
            </DropdownMenuItem>
            <DropdownMenuSeparator className="bg-zinc-800" />
            {!user.moderation?.isBanned && (
              <>
                <DropdownMenuItem
                  className="text-yellow-400 focus:bg-zinc-800 cursor-pointer"
                  onClick={() => onAction('warn', user)}
                >
                  <AlertTriangle className="h-4 w-4 mr-2" />
                  Issue Warning
                </DropdownMenuItem>
                {!user.moderation?.isSuspended ? (
                  <DropdownMenuItem
                    className="text-orange-400 focus:bg-zinc-800 cursor-pointer"
                    onClick={() => onAction('suspend', user)}
                  >
                    <Clock className="h-4 w-4 mr-2" />
                    Suspend User
                  </DropdownMenuItem>
                ) : (
                  <DropdownMenuItem
                    className="text-green-400 focus:bg-zinc-800 cursor-pointer"
                    onClick={() => onAction('unsuspend', user)}
                  >
                    <Clock className="h-4 w-4 mr-2" />
                    Remove Suspension
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem
                  className="text-red-400 focus:bg-zinc-800 cursor-pointer"
                  onClick={() => onAction('ban', user)}
                >
                  <Ban className="h-4 w-4 mr-2" />
                  Ban User
                </DropdownMenuItem>
              </>
            )}
            {user.moderation?.isBanned && (
              <DropdownMenuItem
                className="text-green-400 focus:bg-zinc-800 cursor-pointer"
                onClick={() => onAction('unban', user)}
              >
                <ShieldCheck className="h-4 w-4 mr-2" />
                Unban User
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator className="bg-zinc-800" />
            <DropdownMenuItem
              className="text-blue-400 focus:bg-zinc-800 cursor-pointer"
              onClick={() => onAction('role', user)}
            >
              <Shield className="h-4 w-4 mr-2" />
              Change Role
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}

export default function AdminUsers() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState(
    searchParams.get('search') || ''
  );
  const [selectedUser, setSelectedUser] = useState(null);
  const [actionType, setActionType] = useState(null);
  const [actionReason, setActionReason] = useState('');
  const [suspendDuration, setSuspendDuration] = useState('24');
  const [newRole, setNewRole] = useState('');
  const [editorUser, setEditorUser] = useState(null);
  const { toast } = useToast();

  const currentPage = parseInt(searchParams.get('page') || '1');
  const roleFilter = searchParams.get('role') || 'all';
  const statusFilter = searchParams.get('status') || 'all';

  const { data, isLoading, error, refetch } = useAdminUsers({
    page: currentPage,
    limit: 20,
    search: searchParams.get('search') || '',
    role: roleFilter !== 'all' ? roleFilter : undefined,
    isSuspended: statusFilter === 'suspended' ? true : undefined,
    isBanned: statusFilter === 'banned' ? true : undefined,
  });

  const warnUser = useWarnUser();
  const suspendUser = useSuspendUser();
  const banUser = useBanUser();
  const unbanUser = useUnbanUser();
  const changeRole = useChangeUserRole();

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams(searchParams);
    if (searchQuery) {
      params.set('search', searchQuery);
    } else {
      params.delete('search');
    }
    params.set('page', '1');
    setSearchParams(params);
  };

  const handleFilterChange = (key, value) => {
    const params = new URLSearchParams(searchParams);
    if (value && value !== 'all') {
      params.set(key, value);
    } else {
      params.delete(key);
    }
    params.set('page', '1');
    setSearchParams(params);
  };

  const handlePageChange = (newPage) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', newPage.toString());
    setSearchParams(params);
  };

  const handleAction = (action, user) => {
    if (action === 'edit') {
      setEditorUser(user);
      return;
    }
    if (action === 'view') {
      // Navigate to user profile
      window.open(`/profile/${user.creator || user.walletAddress}`, '_blank');
      return;
    }
    setSelectedUser(user);
    setActionType(action);
    setActionReason('');
    setSuspendDuration('24');
    setNewRole(user.role);
  };

  const closeDialog = () => {
    setSelectedUser(null);
    setActionType(null);
    setActionReason('');
  };

  const closeEditor = () => {
    setEditorUser(null);
    refetch();
  };

  const executeAction = async () => {
    if (!selectedUser) return;

    // Use creator field (wallet address) as the user identifier
    const userId = selectedUser.creator || selectedUser.walletAddress;

    try {
      switch (actionType) {
        case 'warn':
          await warnUser.mutateAsync({
            userId,
            reason: actionReason,
          });
          toast({ title: 'Warning issued successfully' });
          break;
        case 'suspend':
          await suspendUser.mutateAsync({
            userId,
            reason: actionReason,
            duration: parseInt(suspendDuration),
          });
          toast({ title: 'User suspended successfully' });
          break;
        case 'ban':
          await banUser.mutateAsync({
            userId,
            reason: actionReason,
          });
          toast({ title: 'User banned successfully' });
          break;
        case 'unban':
          await unbanUser.mutateAsync({
            userId,
          });
          toast({ title: 'User unbanned successfully' });
          break;
        case 'role':
          await changeRole.mutateAsync({
            userId,
            newRole: newRole,
          });
          toast({ title: 'Role updated successfully' });
          break;
      }
      closeDialog();
      refetch();
    } catch (err) {
      toast({
        title: 'Action failed',
        description: err.message,
        variant: 'destructive',
      });
    }
  };

  const isActionLoading =
    warnUser.isPending ||
    suspendUser.isPending ||
    banUser.isPending ||
    unbanUser.isPending ||
    changeRole.isPending;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white flex items-center gap-2">
          <Users className="h-6 w-6" />
          User Management
        </h1>
        <p className="text-zinc-400">
          Manage users, roles, and moderation actions
        </p>
      </div>

      {/* Filters */}
      <Card className="border border-zinc-800 bg-zinc-900/50">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <form onSubmit={handleSearch} className="flex-1 flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
                <Input
                  placeholder="Search by username or wallet address..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500"
                />
              </div>
              <Button
                type="submit"
                variant="secondary"
                className="bg-zinc-800 hover:bg-zinc-700 text-white"
              >
                Search
              </Button>
            </form>

            <div className="flex gap-2">
              <Select
                value={roleFilter}
                onValueChange={(v) => handleFilterChange('role', v)}
              >
                <SelectTrigger className="w-[130px] bg-zinc-800 border-zinc-700 text-white">
                  <Filter className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Role" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  <SelectItem value="all">All Roles</SelectItem>
                  <SelectItem value="user">User</SelectItem>
                  <SelectItem value="moderator">Moderator</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>

              <Select
                value={statusFilter}
                onValueChange={(v) => handleFilterChange('status', v)}
              >
                <SelectTrigger className="w-[140px] bg-zinc-800 border-zinc-700 text-white">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="suspended">Suspended</SelectItem>
                  <SelectItem value="banned">Banned</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* User List */}
      <Card className="border border-zinc-800 bg-zinc-900/50 overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center">
            <div className="animate-spin h-8 w-8 border-2 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-zinc-400">Loading users...</p>
          </div>
        ) : error ? (
          <div className="p-8 text-center">
            <AlertTriangle className="h-8 w-8 text-red-400 mx-auto mb-4" />
            <p className="text-red-400">{error.message}</p>
          </div>
        ) : data?.users?.length === 0 ? (
          <div className="p-8 text-center">
            <Users className="h-8 w-8 text-zinc-600 mx-auto mb-4" />
            <p className="text-zinc-400">No users found</p>
          </div>
        ) : (
          <>
            <div className="divide-y divide-zinc-800">
              {data?.users?.map((user, index) => (
                <UserRow
                  key={user._id || user.walletAddress || user.creator || index}
                  user={user}
                  onAction={handleAction}
                />
              ))}
            </div>

            {/* Pagination */}
            {data?.pagination && (
              <div className="p-4 border-t border-zinc-800 flex items-center justify-between">
                <p className="text-sm text-zinc-400">
                  Showing {(currentPage - 1) * 20 + 1} to{' '}
                  {Math.min(currentPage * 20, data.pagination.total)} of{' '}
                  {data.pagination.total} users
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="border-zinc-700 bg-zinc-800 hover:bg-zinc-700 text-white"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="px-3 py-1 text-sm text-zinc-400 flex items-center">
                    Page {currentPage} of {data.pagination.pages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage >= data.pagination.pages}
                    className="border-zinc-700 bg-zinc-800 hover:bg-zinc-700 text-white"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </Card>

      {/* Action Dialogs */}
      <Dialog open={actionType === 'warn'} onOpenChange={() => closeDialog()}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-yellow-400" />
              Issue Warning
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              Send a warning to{' '}
              {selectedUser?.username || selectedUser?.walletAddress}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm text-zinc-400">Reason</label>
              <Textarea
                placeholder="Explain why this warning is being issued..."
                value={actionReason}
                onChange={(e) => setActionReason(e.target.value)}
                className="mt-1 bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500"
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={closeDialog}
              className="text-zinc-400 hover:text-white"
            >
              Cancel
            </Button>
            <Button
              onClick={executeAction}
              disabled={!actionReason || isActionLoading}
              className="bg-yellow-500 hover:bg-yellow-600 text-black"
            >
              {isActionLoading ? 'Processing...' : 'Issue Warning'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={actionType === 'suspend'}
        onOpenChange={() => closeDialog()}
      >
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-orange-400" />
              Suspend User
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              Temporarily suspend{' '}
              {selectedUser?.username || selectedUser?.walletAddress}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm text-zinc-400">Duration</label>
              <Select
                value={suspendDuration}
                onValueChange={setSuspendDuration}
              >
                <SelectTrigger className="mt-1 bg-zinc-800 border-zinc-700 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  <SelectItem value="1">1 hour</SelectItem>
                  <SelectItem value="6">6 hours</SelectItem>
                  <SelectItem value="24">24 hours</SelectItem>
                  <SelectItem value="72">3 days</SelectItem>
                  <SelectItem value="168">7 days</SelectItem>
                  <SelectItem value="720">30 days</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm text-zinc-400">Reason</label>
              <Textarea
                placeholder="Explain why this user is being suspended..."
                value={actionReason}
                onChange={(e) => setActionReason(e.target.value)}
                className="mt-1 bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500"
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={closeDialog}
              className="text-zinc-400 hover:text-white"
            >
              Cancel
            </Button>
            <Button
              onClick={executeAction}
              disabled={!actionReason || isActionLoading}
              className="bg-orange-500 hover:bg-orange-600 text-black"
            >
              {isActionLoading ? 'Processing...' : 'Suspend User'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={actionType === 'ban'} onOpenChange={() => closeDialog()}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Ban className="h-5 w-5 text-red-400" />
              Ban User
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              Permanently ban{' '}
              {selectedUser?.username || selectedUser?.walletAddress}. This
              action can be reversed.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm text-zinc-400">Reason</label>
              <Textarea
                placeholder="Explain why this user is being banned..."
                value={actionReason}
                onChange={(e) => setActionReason(e.target.value)}
                className="mt-1 bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500"
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={closeDialog}
              className="text-zinc-400 hover:text-white"
            >
              Cancel
            </Button>
            <Button
              onClick={executeAction}
              disabled={!actionReason || isActionLoading}
              variant="destructive"
              className="bg-red-500 hover:bg-red-600"
            >
              {isActionLoading ? 'Processing...' : 'Ban User'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={actionType === 'unban'} onOpenChange={() => closeDialog()}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-green-400" />
              Unban User
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              Remove the ban on{' '}
              {selectedUser?.username || selectedUser?.walletAddress}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={closeDialog}
              className="text-zinc-400 hover:text-white"
            >
              Cancel
            </Button>
            <Button
              onClick={executeAction}
              disabled={isActionLoading}
              className="bg-green-500 hover:bg-green-600 text-black"
            >
              {isActionLoading ? 'Processing...' : 'Unban User'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={actionType === 'role'} onOpenChange={() => closeDialog()}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-blue-400" />
              Change Role
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              Update role for{' '}
              {selectedUser?.username || selectedUser?.walletAddress}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm text-zinc-400">New Role</label>
              <Select value={newRole} onValueChange={setNewRole}>
                <SelectTrigger className="mt-1 bg-zinc-800 border-zinc-700 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  <SelectItem value="user">User</SelectItem>
                  <SelectItem value="moderator">Moderator</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={closeDialog}
              className="text-zinc-400 hover:text-white"
            >
              Cancel
            </Button>
            <Button
              onClick={executeAction}
              disabled={newRole === selectedUser?.role || isActionLoading}
              className="bg-blue-500 hover:bg-blue-600 text-white"
            >
              {isActionLoading ? 'Processing...' : 'Update Role'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* User Editor Dialog */}
      <UserEditorDialog
        user={editorUser}
        open={!!editorUser}
        onClose={closeEditor}
      />
    </div>
  );
}
